import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Card,
  CardContent,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import ShareIcon from "@mui/icons-material/Share";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Group, GroupMember } from "../lib/types";
import { groupAPI } from "../services/api";
import { GroupSettings, TransferOwnershipModal } from "../components/groups";

const GroupDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [group, setGroup] = useState<Group | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [transferOpen, setTransferOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteInFlight, setDeleteInFlight] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const loadGroup = useCallback(async () => {
    if (!id) {
      return;
    }
    try {
      setLoading(true);
      const [groupData, membersData] = await Promise.all([
        groupAPI.getById(id),
        groupAPI.members.list(id),
      ]);
      setGroup(groupData);
      setMembers(membersData);
    } catch (error) {
      console.error("Failed to load group:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadGroup();
  }, [loadGroup]);

  const handleSave = async (payload: Partial<Group>) => {
    if (!id) {
      return;
    }
    const updated = await groupAPI.update(id, payload);
    setGroup(updated);
  };

  const handleDelete = async () => {
    if (!id) {
      return;
    }
    try {
      setDeleteInFlight(true);
      await groupAPI.remove(id);
      navigate("/groups");
    } catch (error) {
      console.error("Failed to delete group", { groupId: id, error });
      setDeleteError(
        error instanceof Error ? error.message : "Failed to delete group"
      );
    } finally {
      setDeleteInFlight(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleTransfer = async (userId: string) => {
    if (!id) {
      return;
    }
    await groupAPI.transferOwner(id, userId);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Typography color="text.secondary">Loading group...</Typography>
      </Container>
    );
  }

  if (!group) {
    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Typography color="text.secondary">Group not found.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack spacing={3}>
        <Card elevation={0} sx={{ borderRadius: 1 }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h4" fontWeight={800}>
                {group.name}
              </Typography>
              <Typography color="text.secondary">
                {group.description || "No description yet."}
              </Typography>
              <Divider />
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Tooltip title="Manage members">
                  <IconButton
                    component={Link}
                    to={`/groups/${group.id}/members`}
                    onClick={() =>
                      console.log("Navigating to group members", {
                        groupId: group.id,
                      })
                    }
                  >
                    <GroupIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Shared items">
                  <IconButton
                    component={Link}
                    to={`/groups/${group.id}/shared`}
                    onClick={() =>
                      console.log("Navigating to shared items", {
                        groupId: group.id,
                      })
                    }
                  >
                    <ShareIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Deleted records">
                  <IconButton
                    component={Link}
                    to={`/groups/${group.id}/deleted`}
                    onClick={() =>
                      console.log("Navigating to deleted records", {
                        groupId: group.id,
                      })
                    }
                  >
                    <DeleteSweepIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Transfer ownership">
                  <IconButton
                    onClick={() => {
                      console.log("Opening transfer ownership modal", {
                        groupId: group.id,
                      });
                      setTransferOpen(true);
                    }}
                  >
                    <SwapHorizIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <GroupSettings
          group={group}
          onSave={handleSave}
          onDelete={
            group.role === "owner" ? () => setDeleteDialogOpen(true) : undefined
          }
        />
      </Stack>

      <TransferOwnershipModal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        members={members}
        onTransfer={handleTransfer}
      />
      <Dialog
        open={deleteDialogOpen}
        onClose={() => (deleteInFlight ? null : setDeleteDialogOpen(false))}
      >
        <DialogTitle>Delete Group?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteInFlight}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteInFlight}
          >
            {deleteInFlight ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(deleteError)}
        autoHideDuration={5000}
        onClose={() => setDeleteError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setDeleteError(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {deleteError}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default GroupDetailPage;
